import { useEffect, useMemo, useState } from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";
import { SelectInput } from "@web-app/components";
import {
  executeAnalyticsQuery,
  transformCubeDataToChartFormat,
} from "@web-app/services/analytics";
import type {
  ChartDataUnion,
  ChartType,
  CubeQueryConfig,
  CubeQueryResponse,
  GUIBuilderFilter,
} from "@web-app/types";

import { FilterSection } from "./FilterSection";
import { customFieldSlug } from "./SQLEditor";
import {
  DIMENSION_CATEGORIES,
  type DimensionCategory,
  type DimensionOption,
  GUIDED_DIMENSIONS,
  GUIDED_FILTER_FIELDS,
  GUIDED_METRICS,
  METRIC_CATEGORIES,
  type MetricCategory,
  type MetricOption,
  buildCubeQueryFromGuided,
  generateChartTitle,
  getGroupedDimensions,
  getGroupedMetrics,
  suggestChartTypes,
} from "./guidedBuilderConfig";

const CHART_TYPE_LABELS: Partial<Record<ChartType, string>> = {
  area: "Area Chart",
  bar: "Bar Chart",
  column: "Column Chart",
  donut: "Donut Chart",
  gauge: "Gauge",
  line: "Line Chart",
  metric: "Metric Card",
  pie: "Pie Chart",
  table: "Table",
};

interface GUIBuilderProps {
  dashboardDateRange: { from: string; to: string };
  initialQuery?: CubeQueryConfig | null;
  initialType?: ChartType;
  onPreviewUpdate: (
    data: ChartDataUnion | null,
    chartType: ChartType,
    title: string,
  ) => void;
  onQueryUpdate: (query: CubeQueryConfig | null) => void;
}

export default function GUIBuilder({
  dashboardDateRange,
  initialQuery,
  initialType,
  onPreviewUpdate,
  onQueryUpdate,
}: GUIBuilderProps) {
  // Selection state
  const [selectedMetricCategory, setSelectedMetricCategory] = useState<
    string | null
  >(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricOption | null>(
    null,
  );
  const [selectedDimensionCategory, setSelectedDimensionCategory] = useState<
    string | null
  >(null);
  const [selectedDimension, setSelectedDimension] =
    useState<DimensionOption | null>(null);
  const [filters, setFilters] = useState<GUIBuilderFilter[]>([]);
  const [chartType, setChartType] = useState<ChartType>(initialType || "bar");
  const [initialized, setInitialized] = useState(false);

  // Build grouped options
  const groupedMetrics = getGroupedMetrics();
  const groupedDimensions = getGroupedDimensions();

  // Fetch custom fields from current and connected workspaces
  const customFieldsQuery = useWorkspaceActiveCustomFields();
  const customFields = customFieldsQuery.customFields || [];

  // Transform custom fields to dimension options, deduplicating by slug
  const customFieldDimensions: DimensionOption[] = useMemo(() => {
    const seenSlugs = new Set<string>();
    const dimensions: DimensionOption[] = [];

    for (const field of customFields) {
      const slug = customFieldSlug(field.name);

      // Skip if we've already seen this slug
      if (seenSlugs.has(slug)) {
        continue;
      }

      seenSlugs.add(slug);
      dimensions.push({
        category: "custom" as DimensionCategory,
        cubeMapping: {
          cube: "orders",
          dimension: `orders.custom_fields_${slug}`,
        },
        description: `Custom field: ${field.name}`,
        id: `custom-${slug}`,
        label: field.name,
        suggestedChartTypes: [
          "bar",
          "column",
          "pie",
          "donut",
          "table",
        ] as ChartType[],
      });
    }

    return dimensions;
  }, [customFields]);

  // Merge custom field dimensions with grouped dimensions
  const allGroupedDimensions = useMemo(() => {
    const merged = { ...groupedDimensions };
    if (customFieldDimensions.length > 0) {
      merged.custom = customFieldDimensions;
    }
    return merged;
  }, [groupedDimensions, customFieldDimensions]);

  // Build category options
  const metricCategoryOptions = Object.entries(METRIC_CATEGORIES).map(
    ([key, category]) => ({
      label: category.label,
      value: key,
    }),
  );

  const dimensionCategoryOptions = useMemo(() => {
    const baseCategories = Object.entries(DIMENSION_CATEGORIES).map(
      ([key, category]) => ({
        label: category.label,
        value: key,
      }),
    );
    // Add custom fields category if there are any custom fields
    if (customFieldDimensions.length > 0) {
      baseCategories.push({
        label: "Custom Fields",
        value: "custom",
      });
    }
    return baseCategories;
  }, [customFieldDimensions]);

  // Build metric options based on selected category
  const metricOptions = selectedMetricCategory
    ? (groupedMetrics[selectedMetricCategory as MetricCategory] || []).map(
        (metric: MetricOption) => ({
          label: metric.label,
          value: metric.id,
        }),
      )
    : [];

  // Build dimension options based on selected category
  const dimensionOptions = selectedDimensionCategory
    ? [
        { label: "None (Single Value)", value: "" },
        ...(
          allGroupedDimensions[
            selectedDimensionCategory as DimensionCategory
          ] || []
        ).map((dimension: DimensionOption) => ({
          label: dimension.label,
          value: dimension.id,
        })),
      ]
    : [{ label: "None (Single Value)", value: "" }];

  // Auto-suggest chart types based on selections
  const suggestedTypes = suggestChartTypes(selectedMetric, selectedDimension);

  // All chart types
  const allChartTypes: ChartType[] = [
    "metric",
    "gauge",
    "line",
    "bar",
    "column",
    "area",
    "pie",
    "donut",
    "table",
  ];

  // Auto-generate title
  const autoTitle = generateChartTitle(selectedMetric, selectedDimension);

  // Reset initialized flag when initialQuery changes
  useEffect(() => {
    setInitialized(false);
  }, [initialQuery]);

  // Initialize from existing query
  useEffect(() => {
    if (initialQuery && !initialized) {
      // Parse the query to extract measure and dimension
      const measure = initialQuery.measures?.[0];
      const dimension = initialQuery.dimensions?.[0];
      const timeDimension = initialQuery.timeDimensions?.[0];

      if (measure) {
        // Match by cubeMapping.measure instead of id
        let foundMetric: MetricOption | undefined;
        let foundCategory: string | undefined;

        for (const [category, metrics] of Object.entries(groupedMetrics)) {
          const metric = metrics.find((m) => m.cubeMapping.measure === measure);
          if (metric) {
            foundMetric = metric;
            foundCategory = category;
            break;
          }
        }

        if (foundMetric && foundCategory) {
          setSelectedMetricCategory(foundCategory);
          setSelectedMetric(foundMetric);
        }
      }

      // Check for regular dimension first
      if (dimension) {
        // Match by cubeMapping.dimension instead of id
        let foundDimension: DimensionOption | undefined;
        let foundCategory: string | undefined;

        for (const [category, dimensions] of Object.entries(
          allGroupedDimensions,
        )) {
          const dim = dimensions.find(
            (d) => d.cubeMapping.dimension === dimension,
          );
          if (dim) {
            foundDimension = dim;
            foundCategory = category;
            break;
          }
        }

        if (foundDimension && foundCategory) {
          setSelectedDimensionCategory(foundCategory);
          setSelectedDimension(foundDimension);
        }
      } else if (timeDimension?.granularity) {
        // Check for time dimension (these are stored in timeDimensions, not dimensions)
        let foundDimension: DimensionOption | undefined;
        let foundCategory: string | undefined;

        for (const [category, dimensions] of Object.entries(
          allGroupedDimensions,
        )) {
          const dim = dimensions.find(
            (d) =>
              d.cubeMapping.dimension === timeDimension.dimension &&
              d.cubeMapping.granularity === timeDimension.granularity,
          );
          if (dim) {
            foundDimension = dim;
            foundCategory = category;
            break;
          }
        }

        if (foundDimension && foundCategory) {
          setSelectedDimensionCategory(foundCategory);
          setSelectedDimension(foundDimension);
        }
      }

      // Restore filters if they exist
      if (initialQuery.filters && initialQuery.filters.length > 0) {
        const restoredFilters: GUIBuilderFilter[] = initialQuery.filters
          .map((filter) => {
            // Find the field config that matches this filter's member
            const fieldConfig = GUIDED_FILTER_FIELDS.find(
              (f) => f.cubeMapping.dimension === filter.member,
            );

            if (fieldConfig) {
              return {
                field: fieldConfig.id,
                operator: filter.operator,
                values: filter.values || [],
              };
            }
            return null;
          })
          .filter((f) => f !== null) as GUIBuilderFilter[];

        setFilters(restoredFilters);
      }

      setInitialized(true);
    }
  }, [initialQuery, initialized, groupedMetrics, allGroupedDimensions]);

  // Generate preview when selections change
  useEffect(() => {
    if (!selectedMetric) {
      onPreviewUpdate(null, chartType, "");
      onQueryUpdate(null);
      return;
    }

    generatePreview();
  }, [
    selectedMetric,
    selectedDimension,
    filters,
    chartType,
    dashboardDateRange,
  ]);

  const generatePreview = async () => {
    if (!selectedMetric) {
      return;
    }

    try {
      const query = buildCubeQueryFromGuided(
        selectedMetric,
        selectedDimension,
        dashboardDateRange,
        filters,
      );

      const response: CubeQueryResponse = await executeAnalyticsQuery(query);
      const transformed = transformCubeDataToChartFormat(
        response,
        chartType,
        query,
      );

      onPreviewUpdate(transformed, chartType, autoTitle);
      onQueryUpdate(query);
    } catch (error) {
      console.error("Preview failed:", error);
      onPreviewUpdate(null, chartType, "");
      onQueryUpdate(null);
    }
  };

  const handleMetricCategoryChange = (value: string) => {
    setSelectedMetricCategory(value);
    setSelectedMetric(null); // Reset metric when category changes
  };

  const handleMetricChange = (value: string) => {
    const metric = GUIDED_METRICS.find((m) => m.id === value) || null;
    setSelectedMetric(metric);

    // Auto-select first suggested chart type
    if (metric) {
      const suggested = suggestChartTypes(metric, selectedDimension);
      if (suggested.length > 0) {
        setChartType(suggested[0]);
      }
    }
  };

  const handleDimensionCategoryChange = (value: string) => {
    setSelectedDimensionCategory(value);
    setSelectedDimension(null); // Reset dimension when category changes
  };

  const handleDimensionChange = (value: string) => {
    // Search in both GUIDED_DIMENSIONS and custom field dimensions
    const allDimensions = [...GUIDED_DIMENSIONS, ...customFieldDimensions];
    const dimension = allDimensions.find((d) => d.id === value) || null;
    setSelectedDimension(dimension);

    // Auto-select first suggested chart type
    if (selectedMetric) {
      const suggested = suggestChartTypes(selectedMetric, dimension);
      if (suggested.length > 0) {
        setChartType(suggested[0]);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Metric Category Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Metric Category *
        </label>
        <SelectInput
          selected={selectedMetricCategory || ""}
          onChange={handleMetricCategoryChange}
          options={metricCategoryOptions}
          placeholder="Select a category..."
        />
      </div>

      {/* Metric Selection */}
      {selectedMetricCategory && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Metric *
          </label>
          <SelectInput
            selected={selectedMetric?.id || ""}
            onChange={handleMetricChange}
            options={metricOptions}
            placeholder="Select a metric..."
          />
        </div>
      )}

      {/* Dimension Category Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Grouping Category
        </label>
        <SelectInput
          selected={selectedDimensionCategory || ""}
          onChange={handleDimensionCategoryChange}
          options={dimensionCategoryOptions}
          placeholder="Select a category..."
        />
      </div>

      {/* Dimension Selection */}
      {selectedDimensionCategory && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Group By
          </label>
          <SelectInput
            selected={selectedDimension?.id || ""}
            onChange={handleDimensionChange}
            options={dimensionOptions}
          />
        </div>
      )}

      {/* Filter Section */}
      {selectedMetric && (
        <FilterSection filters={filters} onFiltersChange={setFilters} />
      )}

      {/* Chart Type Selection */}
      {selectedMetric && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            How should we display this? *
          </label>

          {/* Suggested Chart Types */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
              Recommended
            </p>
            <div className="grid grid-cols-2 gap-2">
              {suggestedTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setChartType(type)}
                  className={`
                    border-2 rounded-lg p-3 text-left transition
                    ${
                      chartType === type
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300 bg-white"
                    }
                  `}
                >
                  <p className="text-sm font-medium text-gray-900">
                    {CHART_TYPE_LABELS[type]}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* All Other Chart Types */}
          <details className="mt-3">
            <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
              See all chart types ({allChartTypes.length})
            </summary>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {allChartTypes
                .filter((type) => !suggestedTypes.includes(type))
                .map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setChartType(type)}
                    className={`
                      border-2 rounded-lg p-3 text-left transition
                      ${
                        chartType === type
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }
                    `}
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {CHART_TYPE_LABELS[type]}
                    </p>
                  </button>
                ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
